/**
 * Rate Limiting Utility
 * Implements client-side and server-side rate limiting to prevent brute force attacks
 */

import { logSecurityEvent, EVENT_TYPES } from './securityLogger';

// Rate limiting configuration
export const RATE_LIMIT_CONFIG = {
  // Login attempts
  LOGIN_ATTEMPTS: {
    MAX_ATTEMPTS: 5,       // Max attempts per window (increased for debugging)
    WINDOW_DURATION: 15 * 60 * 1000, // 15 minutes
    LOCKOUT_DURATION: 30 * 60 * 1000, // 30 minutes lockout
    PROGRESSIVE_DELAY: true,   // Increase delay with each attempt
    BASE_DELAY: 1000,         // 1 second base delay
    MAX_DELAY: 30000          // 30 seconds max delay
  },
  
  // Password reset attempts
  PASSWORD_RESET_ATTEMPTS: {
    MAX_ATTEMPTS: 3,           // Max attempts per window
    WINDOW_DURATION: 60 * 60 * 1000, // 1 hour
    LOCKOUT_DURATION: 2 * 60 * 60 * 1000, // 2 hours lockout
    PROGRESSIVE_DELAY: true,
    BASE_DELAY: 2000,         // 2 seconds base delay
    MAX_DELAY: 60000          // 60 seconds max delay
  },
  
  // TOTP verification attempts
  TOTP_ATTEMPTS: {
    MAX_ATTEMPTS: 3,           // Max attempts per window
    WINDOW_DURATION: 5 * 60 * 1000, // 5 minutes
    LOCKOUT_DURATION: 15 * 60 * 1000, // 15 minutes lockout
    PROGRESSIVE_DELAY: true,
    BASE_DELAY: 1000,
    MAX_DELAY: 10000
  },
  
  // General API requests
  API_REQUESTS: {
    MAX_ATTEMPTS: 100,         // Max requests per window
    WINDOW_DURATION: 60 * 1000, // 1 minute
    LOCKOUT_DURATION: 5 * 60 * 1000, // 5 minutes lockout
    PROGRESSIVE_DELAY: false,
    BASE_DELAY: 100,
    MAX_DELAY: 5000
  }
};

class RateLimiter {
  constructor() {
    this.attempts = new Map(); // Store attempts by key
    this.lockouts = new Map(); // Store lockouts by key
    this.isInitialized = false;
    
    this.init();
  }

  /**
   * Initialize rate limiter
   */
  init() {
    if (this.isInitialized) return;
    
    this.loadStoredData();
    this.startCleanupTimer();
    this.isInitialized = true;
    
    console.log('🛡️ Rate Limiter initialized');
  }

  /**
   * Load stored rate limit data from localStorage
   */
  loadStoredData() {
    try {
      const stored = localStorage.getItem('dp_duel_rate_limits');
      if (stored) {
        const data = JSON.parse(stored);
        this.attempts = new Map(data.attempts || []);
        this.lockouts = new Map(data.lockouts || []);
      }
    } catch (error) {
      console.error('❌ Failed to load rate limit data:', error);
      this.clearStoredData();
    }
  }

  /**
   * Save rate limit data to localStorage
   */
  saveStoredData() {
    try {
      const data = {
        attempts: Array.from(this.attempts.entries()),
        lockouts: Array.from(this.lockouts.entries()),
        timestamp: Date.now()
      };
      localStorage.setItem('dp_duel_rate_limits', JSON.stringify(data));
    } catch (error) {
      console.error('❌ Failed to save rate limit data:', error);
    }
  }

  /**
   * Clear stored data
   */
  clearStoredData() {
    try {
      localStorage.removeItem('dp_duel_rate_limits');
      this.attempts.clear();
      this.lockouts.clear();
    } catch (error) {
      console.error('❌ Failed to clear rate limit data:', error);
    }
  }

  /**
   * Generate rate limit key
   */
  generateKey(type, identifier) {
    return `${type}:${identifier}`;
  }

  /**
   * Check if action is rate limited
   */
  async isRateLimited(type, identifier, config) {
    const key = this.generateKey(type, identifier);
    const now = Date.now();

    // Check if currently locked out
    if (this.lockouts.has(key)) {
      const lockoutEnd = this.lockouts.get(key);
      if (now < lockoutEnd) {
        const remainingTime = Math.ceil((lockoutEnd - now) / 1000);
        console.warn(`🛡️ Rate limited: ${type} for ${identifier} (${remainingTime}s remaining)`);
        
        // Log rate limit hit
        await logSecurityEvent(EVENT_TYPES.RATE_LIMIT_HIT, null, {
          type: type,
          identifier: identifier,
          remainingTime: remainingTime,
          reason: 'lockout_active'
        });
        
        return {
          isLimited: true,
          remainingTime: remainingTime,
          reason: 'lockout_active'
        };
      } else {
        // Lockout expired, remove it
        this.lockouts.delete(key);
      }
    }

    // Check attempt history
    if (this.attempts.has(key)) {
      const attemptData = this.attempts.get(key);
      const windowStart = now - config.WINDOW_DURATION;
      
      // Filter attempts within current window
      const recentAttempts = attemptData.filter(timestamp => timestamp > windowStart);
      
      if (recentAttempts.length >= config.MAX_ATTEMPTS) {
        // Rate limit exceeded, start lockout
        const lockoutEnd = now + config.LOCKOUT_DURATION;
        this.lockouts.set(key, lockoutEnd);
        
        console.warn(`🛡️ Rate limit exceeded: ${type} for ${identifier} (lockout for ${config.LOCKOUT_DURATION / 1000}s)`);
        
        // Log rate limit exceeded
        await logSecurityEvent(EVENT_TYPES.RATE_LIMIT_EXCEEDED, null, {
          type: type,
          identifier: identifier,
          attempts: recentAttempts.length,
          lockoutDuration: config.LOCKOUT_DURATION / 1000
        });
        
        return {
          isLimited: true,
          remainingTime: Math.ceil(config.LOCKOUT_DURATION / 1000),
          reason: 'rate_limit_exceeded'
        };
      }
    }

    return { isLimited: false };
  }

  /**
   * Record an attempt
   */
  async recordAttempt(type, identifier, config) {
    const key = this.generateKey(type, identifier);
    const now = Date.now();

    // Initialize attempt history if not exists
    if (!this.attempts.has(key)) {
      this.attempts.set(key, []);
    }

    // Add current attempt
    const attemptData = this.attempts.get(key);
    attemptData.push(now);

    // Keep only recent attempts (within window + some buffer)
    const cutoff = now - (config.WINDOW_DURATION * 2);
    const filteredAttempts = attemptData.filter(timestamp => timestamp > cutoff);
    this.attempts.set(key, filteredAttempts);

    // Save to storage
    this.saveStoredData();

    // Log attempt
    await logSecurityEvent(EVENT_TYPES.RATE_LIMIT_ATTEMPT, null, {
      type: type,
      identifier: identifier,
      attemptCount: filteredAttempts.length,
      maxAttempts: config.MAX_ATTEMPTS
    });

    console.log(`🛡️ Recorded attempt: ${type} for ${identifier} (${filteredAttempts.length}/${config.MAX_ATTEMPTS})`);
  }

  /**
   * Get delay for next attempt (progressive delay)
   */
  getDelay(type, identifier, config) {
    if (!config.PROGRESSIVE_DELAY) {
      return config.BASE_DELAY;
    }

    const key = this.generateKey(type, identifier);
    if (!this.attempts.has(key)) {
      return config.BASE_DELAY;
    }

    const attemptData = this.attempts.get(key);
    const now = Date.now();
    const windowStart = now - config.WINDOW_DURATION;
    const recentAttempts = attemptData.filter(timestamp => timestamp > windowStart);
    
    const delay = Math.min(
      config.BASE_DELAY * Math.pow(2, recentAttempts.length - 1),
      config.MAX_DELAY
    );

    return delay;
  }

  /**
   * Clear rate limit data for specific key
   */
  clearRateLimit(type, identifier) {
    const key = this.generateKey(type, identifier);
    this.attempts.delete(key);
    this.lockouts.delete(key);
    this.saveStoredData();
    
    console.log(`🛡️ Cleared rate limit: ${type} for ${identifier}`);
  }

  /**
   * Clear all rate limit data
   */
  clearAllRateLimits() {
    this.attempts.clear();
    this.lockouts.clear();
    this.clearStoredData();
    
    console.log('🛡️ Cleared all rate limits');
  }

  /**
   * Get rate limit status
   */
  getRateLimitStatus(type, identifier, config) {
    const key = this.generateKey(type, identifier);
    const now = Date.now();

    // Check lockout status
    if (this.lockouts.has(key)) {
      const lockoutEnd = this.lockouts.get(key);
      if (now < lockoutEnd) {
        return {
          isLocked: true,
          remainingTime: Math.ceil((lockoutEnd - now) / 1000),
          reason: 'lockout_active'
        };
      }
    }

    // Check attempt count
    if (this.attempts.has(key)) {
      const attemptData = this.attempts.get(key);
      const windowStart = now - config.WINDOW_DURATION;
      const recentAttempts = attemptData.filter(timestamp => timestamp > windowStart);
      
      return {
        isLocked: false,
        attemptCount: recentAttempts.length,
        maxAttempts: config.MAX_ATTEMPTS,
        remainingAttempts: config.MAX_ATTEMPTS - recentAttempts.length,
        nextResetTime: Math.ceil((recentAttempts[0] + config.WINDOW_DURATION - now) / 1000)
      };
    }

    return {
      isLocked: false,
      attemptCount: 0,
      maxAttempts: config.MAX_ATTEMPTS,
      remainingAttempts: config.MAX_ATTEMPTS,
      nextResetTime: 0
    };
  }

  /**
   * Start cleanup timer to remove expired data
   */
  startCleanupTimer() {
    setInterval(() => {
      const now = Date.now();
      
      // Clean up expired attempts
      for (const [key, attempts] of this.attempts.entries()) {
        const cutoff = now - (RATE_LIMIT_CONFIG.LOGIN_ATTEMPTS.WINDOW_DURATION * 2);
        const filteredAttempts = attempts.filter(timestamp => timestamp > cutoff);
        
        if (filteredAttempts.length === 0) {
          this.attempts.delete(key);
        } else {
          this.attempts.set(key, filteredAttempts);
        }
      }
      
      // Clean up expired lockouts
      for (const [key, lockoutEnd] of this.lockouts.entries()) {
        if (now >= lockoutEnd) {
          this.lockouts.delete(key);
        }
      }
      
      // Save cleaned data
      this.saveStoredData();
    }, 5 * 60 * 1000); // Clean up every 5 minutes
  }
}

// Create singleton instance
const rateLimiter = new RateLimiter();

// Export functions for easy use
export const checkRateLimit = (type, identifier, config) => 
  rateLimiter.isRateLimited(type, identifier, config);

export const recordAttempt = (type, identifier, config) => 
  rateLimiter.recordAttempt(type, identifier, config);

export const getDelay = (type, identifier, config) => 
  rateLimiter.getDelay(type, identifier, config);

export const clearRateLimit = (type, identifier) => 
  rateLimiter.clearRateLimit(type, identifier);

export const getRateLimitStatus = (type, identifier, config) => 
  rateLimiter.getRateLimitStatus(type, identifier, config);

export const clearAllRateLimits = () => 
  rateLimiter.clearAllRateLimits();

// Export the class for advanced usage
export default rateLimiter;
